/**
 * Assigns a timeout to a given promise, and rejects with an error if the
 * timeout has passed
 *
 * @param {number} timeoutInMilliseconds The timeout to apply, in milliseconds
 * @param {Promise<T>} promise The original promise to apply the timeout to
 * @returns {Promise<T>} A new promise that wraps around the original one with timeout assigned
 */
export const withTimeout = <T>(timeoutInMilliseconds: number, promise: Promise<T>): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(`Promise timed out in ${timeoutInMilliseconds} ms.`));
    }, timeoutInMilliseconds);
  });

  return Promise.race<T>([promise, timeoutPromise]).finally(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  });
};

export const resolvedPromise = () => new Promise<void>(resolve => resolve());
export const rejectedPromise = <T>() => new Promise<T>((_resolve, reject) => reject());
